/**
 * Per-request CSPRNG nonce for the HTTP auth header families.
 *
 * Every signed HTTP-auth request (`signPlayerAuthHeaders` /
 * `signAccountSelfAuthHeaders`) draws 16 fresh random bytes here and
 * folds them into the BLAKE3 signing payload as its FINAL field, then
 * emits them on the wire as `x-request-nonce` (32 lowercase hex chars).
 * The backend reconstructs the same canonical message with the received
 * nonce and verifies BOTH the ed25519 leg AND the ml-dsa second leg over
 * the identical nonced digest.
 *
 * Why per-request, not per-tab: a fresh CSPRNG nonce per call makes every
 * legitimate request sign structurally-distinct bytes => a distinct
 * signature, so two signs in the same wall-clock millisecond — within one
 * tab OR across two tabs of the same account — can never byte-collide in
 * the server's single-use replay cache. This subsumes the prior
 * monotonic-clock + per-tab-body-nonce + replay-retry scaffolding: the
 * nonce, not `unix_ms` or the body, is now the distinctness discriminator.
 *
 * The value is a replay-cache discriminator, never a secret and never part
 * of an authorization decision, so the `Math.random` fallback for a
 * context that lacks `crypto.getRandomValues` (SSR boot / a sandboxed
 * context) is acceptable: worst case, an actual byte-identical replay is
 * still refused by the server single-use cache.
 */

/** Draw 16 fresh random bytes and return them as 32 lowercase hex chars. */
export function randomRequestNonceHex(): string {
  const buf = new Uint8Array(16);
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) {
      buf[i] = Math.floor(Math.random() * 256);
    }
  }
  let hex = "";
  for (const b of buf) {
    hex += b.toString(16).padStart(2, "0");
  }
  return hex;
}
